import { CallbackAnimationTypes } from './types';
import { gsap, THREE, errorManager } from '@powerplay/common';
export class AnimationsManager {
    fpsManager;
    mixer;
    animations = {};
    actualAnimation = '';
    paused = false;
    crossfadeEndTween;
    defaultAnimationSpeed;
    constructor(object3D, animations, animationClips, defaultAnimationSpeed, fpsManager) {
        this.fpsManager = fpsManager;
        if (!animationClips) {
            throw new Error(errorManager.showBox('Missing animationClips in AnimationsManager'));
        }
        this.defaultAnimationSpeed = defaultAnimationSpeed;
        this.mixer = new THREE.AnimationMixer(object3D);
        for (const [key, value] of Object.entries(animations)) {
            this.setAnimation(key, value, THREE.AnimationClip.findByName(animationClips, key));
        }
        this.mixer.addEventListener('finished', this.onAnimationEvent.bind(this, CallbackAnimationTypes.end));
        this.mixer.addEventListener('loop', this.onAnimationEvent.bind(this, CallbackAnimationTypes.loop));
    }
    getAnimationAction(animation) {
        return this.animations[animation].action;
    }
    addAnimationCallback(animation, type, callback) {
        this.animations[animation].callbacks[type] = callback;
    }
    removeAnimationCallback(animation, type) {
        this.animations[animation].callbacks[type] = undefined;
    }
    removeCallbacksFromAllAnimations() {
        for (const key of Object.keys(this.animations)) {
            this.removeAnimationCallback(key, CallbackAnimationTypes.crossfade);
            this.removeAnimationCallback(key, CallbackAnimationTypes.end);
            this.removeAnimationCallback(key, CallbackAnimationTypes.loop);
        }
    }
    onAnimationEvent(type, event) {
        for (const [key, value] of Object.entries(this.animations)) {
            if (event.action === this.animations[key].action)
                value.callbacks[type]?.(event);
        }
    }
    setAnimation(animation, setting, clip) {
        this.animations[animation] = {
            action: this.mixer.clipAction(clip),
            setting,
            duration: 0,
            callbacks: {
                [CallbackAnimationTypes.end]: undefined,
                [CallbackAnimationTypes.loop]: undefined,
                [CallbackAnimationTypes.crossfade]: undefined
            }
        };
        const { loop, starting } = this.animations[animation].setting;
        this.animations[animation].duration = this.animations[animation].action.getClip().duration;
        if (loop) {
            this.animations[animation].action.play();
        }
        else {
            this.animations[animation].action.setLoop(THREE.LoopOnce, 1);
            this.animations[animation].action.clampWhenFinished = true;
        }
        let weight = 0;
        if (starting) {
            weight = 1;
            this.changeTo(animation);
        }
        this.setWeight(animation, weight);
    }
    setSpeed(speed) {
        this.mixer.timeScale = speed * this.defaultAnimationSpeed;
    }
    setWeight(animation, weight) {
        if (animation === '')
            return;
        this.animations[animation].action.enabled = true;
        this.animations[animation].action.setEffectiveWeight(weight);
    }
    getDuration(animation) {
        const { duration } = this.animations[animation];
        const temp = duration * this.fpsManager.fpsLimit;
        return Math.floor(temp) / this.fpsManager.fpsLimit;
    }
    play(animation) {
        this.animations[animation].action?.play();
    }
    stop(animation) {
        this.animations[animation].action?.stop();
    }
    reset(animation) {
        this.stop(animation);
        if (this.animations[animation].action)
            this.animations[animation].action.time = 0;
    }
    pauseAll = () => {
        this.paused = true;
    };
    unpauseAll = () => {
        this.paused = false;
    };
    changeTo(animation) {
        if (this.actualAnimation === animation)
            return;
        this.setWeight(animation, 1);
        this.setWeight(this.actualAnimation, 0);
        const loopNew = this.animations[animation].setting.loop;
        let loopOld = false;
        if (this.animations[this.actualAnimation]) {
            loopOld = this.animations[animation].setting.loop;
        }
        if (!loopNew || (loopNew && !loopOld) || this.actualAnimation === '') {
            this.play(animation);
        }
        this.actualAnimation = animation;
    }
    changeToPaused(animation) {
        this.changeTo(animation);
        this.animations[animation].action.paused = true;
    }
    manualyUpdateTimeByPercent(animation, percent) {
        let sanitazedPercent = percent;
        if (percent > 100)
            sanitazedPercent = 100;
        if (percent < 0)
            sanitazedPercent = 0;
        const time = this.animations[animation].duration / 100 * sanitazedPercent;
        this.animations[animation].action.time = time;
    }
    manualCrossfadeTo(animationFrom, animationTo, percent) {
        this.setWeight(animationFrom, 1 - percent);
        this.setWeight(animationTo, percent);
    }
    crossfadeTo(animation, time, forced, keepTime) {
        if (this.actualAnimation === animation)
            return;
        if (forced) {
            this.executeCrossfade(this.actualAnimation, animation, time, keepTime);
        }
        else {
            this.synchronizeCrossfade(this.actualAnimation, animation, time, keepTime);
        }
        this.actualAnimation = animation;
    }
    executeCrossfade(animationFrom, animationTo, time, keepTime) {
        this.setWeight(animationTo, 1);
        if (keepTime) {
            this.animations[animationTo].action.time = this.animations[animationFrom].action.time;
        }
        else {
            this.animations[animationTo].action.time = 0;
        }
        this.animations[animationFrom].action.crossFadeTo(this.animations[animationTo].action, time, false);
        this.crossfadeEndTween = gsap.to({}, {
            onComplete: this.stopCrossfade,
            onCompleteParams: [animationFrom, animationTo],
            callbackScope: this,
            duration: time / this.mixer.timeScale
        });
    }
    synchronizeCrossfade(animationFrom, animationTo, time, keepTime) {
        const callback = () => {
            this.removeAnimationCallback(animationFrom, CallbackAnimationTypes.loop);
            this.executeCrossfade(animationFrom, animationTo, time, keepTime);
        };
        this.addAnimationCallback(animationFrom, CallbackAnimationTypes.loop, callback);
    }
    getAnimationPercentageDone(animationName = this.getActualAnimation()) {
        const animationDuration = this.getDuration(animationName);
        const actualAnimationTime = this.animations[animationName].action.time / animationDuration;
        return actualAnimationTime * 100;
    }
    getAnimationActualTime(animationName = this.getActualAnimation()) {
        return this.animations[animationName].action.time;
    }
    stopCrossfade(animationFrom, animationTo) {
        this.animations[animationFrom].action.reset();
        if (this.crossfadeEndTween) {
            this.crossfadeEndTween.kill();
        }
        this.setWeight(animationFrom, 0);
        this.setWeight(animationTo, 1);
        this.animations[animationTo].callbacks[CallbackAnimationTypes.crossfade]?.(null);
    }
    update(delta) {
        const coef = this.paused ? 0 : 1;
        this.mixer.update(delta * coef);
    }
    resetAll() {
        if (this.crossfadeEndTween) {
            this.crossfadeEndTween.kill();
        }
        for (const [key, value] of Object.entries(this.animations)) {
            this.setWeight(key, 0);
            if (value.setting.starting) {
                this.setWeight(key, 1);
                this.actualAnimation = key;
            }
            if (value.setting.loop) {
                this.stop(key);
                this.play(key);
            }
            else {
                this.reset(key);
            }
        }
        this.paused = false;
        this.setSpeed(1);
    }
    getActualAnimation() {
        return this.actualAnimation;
    }
    setDefaultSpeed(speed) {
        this.defaultAnimationSpeed = speed;
    }
    freezeAnimationToEndOfAnimaton(animation) {
        this.animations[animation].action.time = this.getDuration(animation);
        this.animations[animation].action.paused = true;
    }
    resetSpeed() {
        this.mixer.timeScale = this.defaultAnimationSpeed;
    }
    resetAnimationTime(animation) {
        this.animations[animation].action.time = 0;
    }
    resetActualAnimation() {
        if (!this.actualAnimation)
            return;
        this.reset(this.actualAnimation);
        this.actualAnimation = '';
    }
}
//# sourceMappingURL=AnimationsManager.js.map